import 'react-toastify/dist/ReactToastify.minimal.css';
import './App.scss';
import './styles/global/_actionsheet.scss';
import './styles/global/_autosuggest.scss';
import './styles/global/_base.scss';
import './styles/global/_forms.scss';
import './styles/global/_tag.scss';
import './styles/global/_tooltip.scss';
import './styles/global/_unhide.scss';
import './styles/global/_utils.scss';
import './styles/tailwind.css';
import './styles/vendors/react-toastify/main.scss';

import NpsModal from '@/components/modules/modals/NpsModal';
import { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Slide, toast, ToastContainer } from 'react-toastify';
import LoadingBar from 'react-top-loading-bar';
import { featurePromoActions } from './actions/featurePromoActions';
import useStartupEvents from './App.hooks';
import AppPromotionCard from './components/elements/Card/AppPromotionCard';
import FeaturePromo, {
  checkIfClosedPreviously,
  checkIfScreenBlocked,
  FeaturePromoProps,
} from './components/elements/FeaturePromo/FeaturePromo';
import LoadingPlaceHolder from './components/elements/LoadingPlaceholder';
import { AlertPortal } from './components/modules/alert/AlertPortal';
import AppRouter from './components/modules/app/AppRouter';
import AppRoutes from './components/modules/app/AppRoutes';
import { AllowCookies } from './components/modules/modals';
import AppPromotionModal from './components/modules/modals/AppPromotionModal';
import LastMinuteModal from './components/modules/modals/LastMinuteModal';
import LoginModal from './components/modules/modals/LoginModal';
import UserProfileOnboarding from './components/modules/modals/UserProfileOnboarding';
import featurePromoConfig from './config/feature-promos.json';
import { Z_INDEX } from './constants/styleConstants';
import AssociationDrawer from './features/associations-drawer/AssociationDrawer';
import { useAssocationDrawerContext } from './features/associations-drawer/AssociationDrawer.hooks';
import { getCookie, isServer, isSistaminuten, renameCookie } from './helpers';
import { useAppSelector } from './hooks';
import useMobileView from './hooks/useMobileView';
import useSaveGiftcardFromLink from './hooks/useSaveGiftcardFromLink';

export type AppProps = {
  isMobile?: boolean;
  location?: any;
  context: any;
};

const App = ({ isMobile = false }: AppProps) => {
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(true);
  const [featurePromos] = useState(featurePromoConfig);
  const featurePromoTrigger = useAppSelector((state) => state.featurePromos)?.featurePromoTrigger;
  const loading = useAppSelector((state) => state.loading);
  const flags = useAppSelector((state) => state.flags);
  const { nps, redirect, login, userProfileOnboarding, lastMinute } = useAppSelector((state) => state.modal);
  const { isMobileView } = useMobileView();

  // effects
  useStartupEvents(isMobile, setVisible);
  useSaveGiftcardFromLink();

  // computed
  const loadingbarProps = {
    color: isSistaminuten() ? '#ffcb2e' : '#23A994',
    progress: !loading.is404 && loading.show ? 50 : 100,
  };
  const cookieAcceptedOldName = Boolean(getCookie('CookiePreferencesMP'));
  const cookieAccepted = Boolean(getCookie('BokadirektCookiePreferencesMP'));
  const hasSavedGiftcard = !isServer ? sessionStorage.getItem('savedGiftcard') === 'true' : undefined;
  const hideCookiesModal = cookieAccepted || cookieAcceptedOldName;

  const showLoginModal = login?.show;
  const showCompleteProfileModal = !showLoginModal && userProfileOnboarding?.show;
  const showLastMinuteModal = lastMinute?.show && hideCookiesModal && !showLoginModal && !showCompleteProfileModal;
  const showNpsModal =
    nps?.show && hideCookiesModal && !showLoginModal && !showCompleteProfileModal && !showLastMinuteModal;
  const showAppQrCode =
    !showLoginModal && !showCompleteProfileModal && hideCookiesModal && hasSavedGiftcard && !isMobile;

  const showFeaturePromos = !showLastMinuteModal && featurePromos.length > 0;
  const showAppPromotionCard = !showLoginModal && !showCompleteProfileModal && hideCookiesModal && !showFeaturePromos;

  if (cookieAcceptedOldName) renameCookie('CookiePreferencesMP', 'BokadirektCookiePreferencesMP');

  const triggerFeaturePromotion = () =>
    dispatch(featurePromoActions.updateFeaturePromoTrigger({ id: 'sistaminuten_v2' }));

  useEffect(() => {
    if (hideCookiesModal) {
      setTimeout(() => {
        triggerFeaturePromotion();
      }, 500);
    }
  }, []);

  return (
    <div className="App" style={{ visibility: visible ? 'visible' : 'hidden' }}>
      {!loading.loadingBarHidden && <LoadingBar transitionTime={300} {...loadingbarProps} />}

      {/* Modals */}
      {!hideCookiesModal && <AllowCookies onAllowCallback={triggerFeaturePromotion} />}
      {showAppPromotionCard && <AppPromotionCard />}
      {showLoginModal && <LoginModal isOpen source={login?.triggerSource} />}
      {showCompleteProfileModal && <UserProfileOnboarding isOpen source={login?.triggerSource} />}
      {showNpsModal && <NpsModal isOpen />}
      {showLastMinuteModal && <LastMinuteModal isOpen />}
      {showAppQrCode && <AppPromotionModal />}

      {showFeaturePromos &&
        featurePromos.map((promo) => {
          if (
            isServer ||
            !promo.id ||
            checkIfClosedPreviously(promo.id) ||
            checkIfScreenBlocked(promo?.blockScreens ?? [])
          ) {
            return null;
          }

          const config = isMobileView ? promo?.['mobile'] : promo?.['desktop'];
          const start = Boolean(featurePromoTrigger[promo?.id]?.triggered);

          const props: FeaturePromoProps = {
            key: promo?.id,
            id: promo.id,
            start,
            ...config,
          };

          return <FeaturePromo {...props} />;
        })}

      {/* Router */}
      <Suspense fallback={<LoadingPlaceHolder className="fixed h-screen" />}>
        <AppRoutes redirect={redirect} />
      </Suspense>

      {/* Portals */}
      <AssociationDrawer {...useAssocationDrawerContext()} />
      <AlertPortal />
      <ToastContainer
        limit={1}
        draggable={false}
        closeOnClick={false}
        position={toast.POSITION.BOTTOM_CENTER}
        autoClose={4000}
        closeButton={false}
        transition={Slide}
        style={{ zIndex: Z_INDEX.SNACKBAR }}
      />
    </div>
  );
};

export default function AppWithRouter(props: AppProps) {
  return (
    <AppRouter {...{ location: props.location, context: props }}>
      <App {...props} />
    </AppRouter>
  );
}
